import React, { useEffect, useState } from 'react';
import { Modal, Button, Select } from 'antd';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent, DragStartEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CourseGetTeachingVehicle, CourseTeachingVehiclerSave, CourseGetList, DuplicateTeachingVehicle } from 'services/courseService';
import toast from 'react-hot-toast';
import { GripVertical, RefreshCw, Trash2, Plus, Copy } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLicenseClasses } from 'store/licenseSlice';
import { AppDispatch, RootState } from 'store';

// Suppress ResizeObserver errors
if (typeof window !== 'undefined') {
	const resizeObserverErr = window.console.error;
	window.console.error = (...args: any[]) => {
		if (args[0]?.includes?.('ResizeObserver')) return;
		resizeObserverErr(...args);
	};
}

interface Teaching {
	id: string;
	courseId: string;
	teacherId: string;
	teacherName: string;
	orderNumber: number | null;
}

interface Vehicle {
	id: string;
	courseId: string;
	vehicleId: string;
	vehiclePlateName: string;
	orderNumber: number | null;
}

interface LinkTeacherVehicleModalProps {
	visible: boolean;
	onClose: () => void;
	courseId: string;
}

interface LinkedRow {
	orderNumber: number;
	teaching: Teaching | null;
	vehicles: Vehicle[];
}

// Draggable Teacher Component
const DraggableTeacher = ({ teacher, isInLinkedTable }: { teacher: Teaching; isInLinkedTable: boolean }) => {
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: `teacher-${teacher.id}`,
		data: { type: 'teacher', teacher, isInLinkedTable },
	});

	const style = {
		transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
		opacity: isDragging ? 0.5 : 1,
		userSelect: 'none' as const,
		WebkitUserSelect: 'none' as const,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="cursor-grab active:cursor-grabbing select-none"
			onMouseDown={(e) => e.preventDefault()}>
			<span className="pointer-events-none">{teacher.teacherName}</span>
		</div>
	);
};

// Draggable Vehicle Component
const DraggableVehicle = ({ vehicle, isInLinkedTable }: { vehicle: Vehicle; isInLinkedTable: boolean }) => {
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: `vehicle-${vehicle.id}`,
		data: { type: 'vehicle', vehicle, isInLinkedTable },
	});

	const style = {
		transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
		opacity: isDragging ? 0.5 : 1,
		userSelect: 'none' as const,
		WebkitUserSelect: 'none' as const,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="cursor-grab active:cursor-grabbing select-none"
			onMouseDown={(e) => e.preventDefault()}>
			<span className="pointer-events-none">{vehicle.vehiclePlateName}</span>
		</div>
	);
};

// Droppable Teacher Cell
const DroppableTeacherCell = ({ orderNumber, teacher, onRemove }: { orderNumber: number; teacher: Teaching | null; onRemove: () => void }) => {
	const { setNodeRef, isOver } = useDroppable({
		id: `teacher-cell-${orderNumber}`,
		data: { type: 'teacher-cell', orderNumber },
	});

	return (
		<td
			ref={setNodeRef}
			className={`px-4 py-3 text-sm ${isOver ? 'bg-blue-50' : ''}`}>
			{teacher ? (
				<div className="flex items-center justify-between">
					<DraggableTeacher teacher={teacher} isInLinkedTable={true} />
					<button
						onClick={onRemove}
						className="text-red-500 hover:text-red-700 ml-2">
						<Trash2 size={16} />
					</button>
				</div>
			) : (
				<span className="text-gray-400 italic">Trống</span>
			)}
		</td>
	);
};

// Droppable Vehicle Cell
const DroppableVehicleCell = ({
	orderNumber,
	vehicles,
	onRemove,
}: {
	orderNumber: number;
	vehicles: Vehicle[];
	onRemove: (vehicleId: string) => void;
}) => {
	const { setNodeRef, isOver } = useDroppable({
		id: `vehicle-cell-${orderNumber}`,
		data: { type: 'vehicle-cell', orderNumber },
	});

	return (
		<td
			ref={setNodeRef}
			className={`px-4 py-3 text-sm ${isOver ? 'bg-blue-50' : ''}`}>
			{vehicles.length > 0 ? (
				<div className="space-y-2">
					{vehicles.map((vehicle) => (
						<div key={vehicle.id} className="flex items-center justify-between rounded bg-gray-50 px-2 py-1">
							<DraggableVehicle vehicle={vehicle} isInLinkedTable={true} />
							<button
								onClick={() => onRemove(vehicle.id)}
								className="text-red-500 hover:text-red-700 ml-2">
								<Trash2 size={16} />
							</button>
						</div>
					))}
				</div>
			) : (
				<span className="text-gray-400 italic">Trống</span>
			)}
		</td>
	);
};

const SortableRow = ({ id, children }: any) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<tr ref={setNodeRef} style={style} {...attributes}>
			{children(listeners)}
		</tr>
	);
};

const LinkTeacherVehicleModal: React.FC<LinkTeacherVehicleModalProps> = ({ visible, onClose, courseId }) => {
	const [teachings, setTeachings] = useState<Teaching[]>([]);
	const [vehicles, setVehicles] = useState<Vehicle[]>([]);
	const [linkedRows, setLinkedRows] = useState<LinkedRow[]>([]);
	const [unlinkedTeachers, setUnlinkedTeachers] = useState<Teaching[]>([]);
	const [unlinkedVehicles, setUnlinkedVehicles] = useState<Vehicle[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [dataLoaded, setDataLoaded] = useState(false);
	const [activeId, setActiveId] = useState<string | null>(null);

	// Duplicate functionality states
	const [selectedLicenseClassId, setSelectedLicenseClassId] = useState<string | null>(null);
	const [selectedTargetCourseId, setSelectedTargetCourseId] = useState<string | null>(null);
	const [duplicateOption, setDuplicateOption] = useState<number>(3);
	const [courseList, setCourseList] = useState<any[]>([]);
	const [loadingCourses, setLoadingCourses] = useState(false);
	const [duplicating, setDuplicating] = useState(false);

	const dispatch = useDispatch<AppDispatch>();
	const { items: licenseClasses, loading: loadingLicenseClasses } = useSelector((state: RootState) => state.licenseClasses);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		})
	);

	const renumberRows = (rows: LinkedRow[]) => rows.map((row, idx) => ({ ...row, orderNumber: idx + 1 }));

	useEffect(() => {
		if (visible && !dataLoaded) {
			loadData();
		}
	}, [visible, courseId, dataLoaded]);

	// Fetch license classes if not already loaded
	useEffect(() => {
		if (visible && licenseClasses.length === 0) {
			dispatch(fetchLicenseClasses());
		}
	}, [visible, licenseClasses.length, dispatch]);

	// Fetch courses when license class is selected
	useEffect(() => {
		if (selectedLicenseClassId) {
			loadCourses();
		} else {
			setCourseList([]);
			setSelectedTargetCourseId(null);
		}
	}, [selectedLicenseClassId]);

	const loadData = async () => {
		setLoading(true);
		try {
			const response = await CourseGetTeachingVehicle(courseId);
			if (response.result) {
				const teachingsData: Teaching[] = response.result.teachings || [];
				const vehiclesData: Vehicle[] = response.result.vehicles || [];

				setTeachings(teachingsData);
				setVehicles(vehiclesData);
				organizeData(teachingsData, vehiclesData);
				setDataLoaded(true);
				toast.success('Tải dữ liệu thành công');
			}
		} catch (error: any) {
			toast.error('Lỗi khi tải dữ liệu: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	const loadCourses = async () => {
		setLoadingCourses(true);
		try {
			const response = await CourseGetList({
				courseId: courseId,
				licenseClassId: selectedLicenseClassId
			});
			if (response.result && response.result.items) {
				setCourseList(response.result.items);
			}
		} catch (error: any) {
			toast.error('Lỗi khi tải danh sách khóa học: ' + error.message);
		} finally {
			setLoadingCourses(false);
		}
	};

	const handleDuplicate = async () => {
		if (!selectedTargetCourseId) {
			toast.error('Vui lòng chọn khóa học đích');
			return;
		}

		setDuplicating(true);
		try {
			await DuplicateTeachingVehicle({
				sourceCourseId: selectedTargetCourseId,
				targetCourseId: courseId,
				option: duplicateOption
			});
			toast.success('Sao chép liên kết thành công');
			// Reload data after duplication
			setDataLoaded(false);
			await loadData();
		} catch (error: any) {
			toast.error('Lỗi khi sao chép: ' + error.message);
		} finally {
			setDuplicating(false);
		}
	};

	const organizeData = (teachingsData: Teaching[], vehiclesData: Vehicle[]) => {
		const linkedTeachers = teachingsData.filter((t) => t.orderNumber !== null);
		const linkedVehicles = vehiclesData.filter((v) => v.orderNumber !== null);

		const unlinkedTeachersList = teachingsData.filter((t) => t.orderNumber === null);
		const unlinkedVehiclesList = vehiclesData.filter((v) => v.orderNumber === null);

		const teachersByOrder = new Map<number, Teaching[]>();
		linkedTeachers.forEach((teacher) => {
			const order = teacher.orderNumber!;
			teachersByOrder.set(order, [...(teachersByOrder.get(order) || []), teacher]);
		});

		const vehiclesByOrder = new Map<number, Vehicle[]>();
		linkedVehicles.forEach((vehicle) => {
			const order = vehicle.orderNumber!;
			vehiclesByOrder.set(order, [...(vehiclesByOrder.get(order) || []), vehicle]);
		});

		const teacherOrders = Array.from(teachersByOrder.keys());
		const vehicleOrders = Array.from(vehiclesByOrder.keys());
		const allOrders = Array.from(new Set([...teacherOrders, ...vehicleOrders])).sort((a, b) => a - b);

		const rows: LinkedRow[] = allOrders.map((order, idx) => {
			const teachersAtOrder = teachersByOrder.get(order) || [];
			const vehiclesAtOrder = vehiclesByOrder.get(order) || [];

			if (teachersAtOrder.length > 1) {
				unlinkedTeachersList.push(...teachersAtOrder.slice(1).map((t) => ({ ...t, orderNumber: null })));
			}

			return {
				orderNumber: idx + 1,
				teaching: teachersAtOrder[0] || null,
				vehicles: vehiclesAtOrder,
			};
		});

		setLinkedRows(rows);
		setUnlinkedTeachers(unlinkedTeachersList);
		setUnlinkedVehicles(unlinkedVehiclesList);
	};

	const handleAddAllTeachers = () => {
		if (unlinkedTeachers.length === 0) return;

		const newRows = [...linkedRows];
		const teachersToAdd = [...unlinkedTeachers];

		teachersToAdd.forEach((teacher) => {
			// Find first empty row for teacher
			const emptyRowIndex = newRows.findIndex((row) => !row.teaching);
			if (emptyRowIndex !== -1) {
				newRows[emptyRowIndex].teaching = teacher;
			} else {
				// Add new row
				newRows.push({
					orderNumber: newRows.length + 1,
					teaching: teacher,
					vehicles: [],
				});
			}
		});

		setLinkedRows(newRows);
		setUnlinkedTeachers([]);
	};

	const handleAddAllVehicles = () => {
		if (unlinkedVehicles.length === 0) return;

		const newRows = [...linkedRows];
		const vehiclesToAdd = [...unlinkedVehicles];
		let targetRowIndex = newRows.findIndex((row) => row.teaching);

		if (targetRowIndex === -1 && newRows.length > 0) {
			targetRowIndex = 0;
		}

		if (targetRowIndex === -1) {
			newRows.push({
				orderNumber: 1,
				teaching: null,
				vehicles: [],
			});
			targetRowIndex = 0;
		}

		vehiclesToAdd.forEach((vehicle) => {
			newRows[targetRowIndex].vehicles.push(vehicle);
		});

		setLinkedRows(newRows);
		setUnlinkedVehicles([]);
	};

	const handleRemoveTeacher = (orderNumber: number) => {
		const newRows = [...linkedRows];
		const rowIndex = newRows.findIndex((row) => row.orderNumber === orderNumber);

		if (rowIndex !== -1 && newRows[rowIndex].teaching) {
			setUnlinkedTeachers([...unlinkedTeachers, newRows[rowIndex].teaching!]);
			newRows[rowIndex].teaching = null;

			// Remove row if both teacher and vehicle are null
			if (newRows[rowIndex].vehicles.length === 0) {
				newRows.splice(rowIndex, 1);
			}

			setLinkedRows(renumberRows(newRows));
		}
	};

	const handleRemoveVehicle = (orderNumber: number, vehicleId: string) => {
		const newRows = [...linkedRows];
		const rowIndex = newRows.findIndex((row) => row.orderNumber === orderNumber);

		if (rowIndex !== -1) {
			const vehicleIndex = newRows[rowIndex].vehicles.findIndex((v) => v.id === vehicleId);
			if (vehicleIndex === -1) return;

			const removedVehicle = newRows[rowIndex].vehicles[vehicleIndex];
			setUnlinkedVehicles([...unlinkedVehicles, removedVehicle]);
			newRows[rowIndex].vehicles.splice(vehicleIndex, 1);

			// Remove row if both teacher and vehicle are null
			if (!newRows[rowIndex].teaching && newRows[rowIndex].vehicles.length === 0) {
				newRows.splice(rowIndex, 1);
			}

			setLinkedRows(renumberRows(newRows));
		}
	};

	const handleAddTeacherToRow = (teacher: Teaching) => {
		const emptyRowIndex = linkedRows.findIndex((row) => !row.teaching);
		const newRows = [...linkedRows];

		if (emptyRowIndex !== -1) {
			newRows[emptyRowIndex].teaching = teacher;
		} else {
			newRows.push({
				orderNumber: newRows.length + 1,
				teaching: teacher,
				vehicles: [],
			});
		}

		setLinkedRows(newRows);
		setUnlinkedTeachers(unlinkedTeachers.filter((t) => t.id !== teacher.id));
	};

	const handleAddVehicleToRow = (vehicle: Vehicle) => {
		const newRows = [...linkedRows];
		let targetRowIndex = newRows.findIndex((row) => row.teaching);

		if (targetRowIndex === -1 && newRows.length > 0) {
			targetRowIndex = 0;
		}

		if (targetRowIndex !== -1) {
			newRows[targetRowIndex].vehicles.push(vehicle);
		} else {
			newRows.push({
				orderNumber: newRows.length + 1,
				teaching: null,
				vehicles: [vehicle],
			});
		}

		setLinkedRows(newRows);
		setUnlinkedVehicles(unlinkedVehicles.filter((v) => v.id !== vehicle.id));
	};

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id as string);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveId(null);

		if (!over) {
			return;
		}

		const activeData = active.data.current;
		const overData = over.data.current;

		// Case 1: Dragging row to reorder
		if (!activeData && !overData) {
			const oldIndex = linkedRows.findIndex((row) => row.orderNumber === active.id);
			const newIndex = linkedRows.findIndex((row) => row.orderNumber === over.id);

			if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
				const newRows = arrayMove(linkedRows, oldIndex, newIndex);
				newRows.forEach((row, idx) => {
					row.orderNumber = idx + 1;
				});
				setLinkedRows(newRows);
			}
			return;
		}

		// Case 2: Dragging teacher
		if (activeData?.type === 'teacher' && overData?.type === 'teacher-cell') {
			const teacher = activeData.teacher as Teaching;
			const targetOrderNumber = overData.orderNumber;
			const isFromLinked = activeData.isInLinkedTable;

			let newRows = [...linkedRows];
			const targetRowIndex = newRows.findIndex((row) => row.orderNumber === targetOrderNumber);

			if (targetRowIndex === -1) return;

			const oldTeacher = newRows[targetRowIndex].teaching;

			// If dragging the same teacher to the same position, do nothing
			if (oldTeacher && oldTeacher.id === teacher.id) {
				return;
			}

			let newUnlinkedTeachers = [...unlinkedTeachers];

			// Remove teacher from source position
			if (isFromLinked) {
				const sourceRowIndex = newRows.findIndex((row) => row.teaching?.id === teacher.id);
				if (sourceRowIndex !== -1 && sourceRowIndex !== targetRowIndex) {
					// HOÁN ĐỔI: Swap positions instead of removing
					newRows[sourceRowIndex].teaching = oldTeacher;
					newRows[targetRowIndex].teaching = teacher;
				}
			} else {
				// From unlinked list - move old teacher to unlinked
				newRows[targetRowIndex].teaching = teacher;

				// Remove from unlinked list
				newUnlinkedTeachers = newUnlinkedTeachers.filter((t) => t.id !== teacher.id);

				// Move old teacher to unlinked if exists
				if (oldTeacher) {
					newUnlinkedTeachers.push(oldTeacher);
				}
			}

			// Batch state updates
			setLinkedRows(newRows);
			setUnlinkedTeachers(newUnlinkedTeachers);
		}

		// Case 3: Dragging vehicle
		if (activeData?.type === 'vehicle' && overData?.type === 'vehicle-cell') {
			const vehicle = activeData.vehicle as Vehicle;
			const targetOrderNumber = overData.orderNumber;
			const isFromLinked = activeData.isInLinkedTable;

			let newRows = [...linkedRows];
			const targetRowIndex = newRows.findIndex((row) => row.orderNumber === targetOrderNumber);

			if (targetRowIndex === -1) return;

			let newUnlinkedVehicles = [...unlinkedVehicles];

			// Move vehicle from linked rows to target row
			if (isFromLinked) {
				const sourceRowIndex = newRows.findIndex((row) => row.vehicles.some((v) => v.id === vehicle.id));
				if (sourceRowIndex === -1) {
					return;
				}

				if (sourceRowIndex === targetRowIndex) {
					return;
				}

				newRows[sourceRowIndex].vehicles = newRows[sourceRowIndex].vehicles.filter((v) => v.id !== vehicle.id);
				if (!newRows[targetRowIndex].vehicles.some((v) => v.id === vehicle.id)) {
					newRows[targetRowIndex].vehicles = [...newRows[targetRowIndex].vehicles, vehicle];
				}

				newRows = newRows.filter((row) => row.teaching || row.vehicles.length > 0);
			} else {
				if (newRows[targetRowIndex].vehicles.some((v) => v.id === vehicle.id)) {
					return;
				}

				newRows[targetRowIndex].vehicles = [...newRows[targetRowIndex].vehicles, vehicle];

				// Remove from unlinked list
				newUnlinkedVehicles = newUnlinkedVehicles.filter((v) => v.id !== vehicle.id);
			}

			// Batch state updates
			setLinkedRows(renumberRows(newRows));
			setUnlinkedVehicles(newUnlinkedVehicles);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const teachingsData = linkedRows
				.filter((row) => row.teaching)
				.map((row) => ({
					id: row.teaching!.id,
					courseId: row.teaching!.courseId,
					teacherId: row.teaching!.teacherId,
					orderNumber: row.orderNumber,
				}));

			const vehiclesData = linkedRows.flatMap((row) =>
				row.vehicles.map((vehicle) => ({
					id: vehicle.id,
					courseId: vehicle.courseId,
					vehicleId: vehicle.vehicleId,
					orderNumber: row.orderNumber,
				}))
			);

			// Add unlinked teachers and vehicles with orderNumber = null
			unlinkedTeachers.forEach((teacher) => {
				teachingsData.push({
					id: teacher.id,
					courseId: teacher.courseId,
					teacherId: teacher.teacherId,
					orderNumber: null as any,
				});
			});

			unlinkedVehicles.forEach((vehicle) => {
				vehiclesData.push({
					id: vehicle.id,
					courseId: vehicle.courseId,
					vehicleId: vehicle.vehicleId,
					orderNumber: null as any,
				});
			});

			const data = {
				teachings: teachingsData,
				vehicles: vehiclesData,
			};

			await CourseTeachingVehiclerSave(data);
			toast.success('Cập nhật liên kết thành công');
			onClose();
		} catch (error: any) {
			toast.error('Lỗi khi cập nhật: ' + error.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			title="Liên kết Giáo viên - Xe"
			open={visible}
			onCancel={onClose}
			width={1200}
			footer={[
				<Button key="reload" icon={<RefreshCw size={16} />} onClick={loadData} loading={loading}>
					Tải lại
				</Button>,
				<Button key="cancel" onClick={onClose}>
					Hủy
				</Button>,
				<Button key="save" type="primary" onClick={handleSave} loading={saving}>
					Cập nhật
				</Button>,
			]}>
			<div className="space-y-6">
				{/* Linked Table */}
				<div>
					<h3 className="text-lg font-semibold mb-3">Bảng liên kết (Kéo thả để sắp xếp)</h3>
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
						<SortableContext items={linkedRows.map((row) => row.orderNumber)} strategy={verticalListSortingStrategy}>
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200 border">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase w-12"></th>
											<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16">STT</th>
											<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[40%]">
												Giáo viên
											</th>
											<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[40%]">
												Phương tiện
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{linkedRows.length === 0 ? (
											<tr>
												<td colSpan={4} className="px-6 py-4 text-center text-gray-500">
													Chưa có dữ liệu liên kết. Kéo GV hoặc PT từ bảng bên dưới vào đây.
												</td>
											</tr>
										) : (
											linkedRows.map((row) => (
												<SortableRow key={row.orderNumber} id={row.orderNumber}>
													{(listeners: any) => (
														<>
															<td className="px-2 py-3 text-center">
																<div {...listeners} className="cursor-move">
																	<GripVertical size={18} className="text-gray-400" />
																</div>
															</td>
															<td className="px-4 py-3 text-center text-sm">{row.orderNumber}</td>
															<DroppableTeacherCell
																orderNumber={row.orderNumber}
																teacher={row.teaching}
																onRemove={() => handleRemoveTeacher(row.orderNumber)}
															/>
															<DroppableVehicleCell
																orderNumber={row.orderNumber}
																																		vehicles={row.vehicles}
																																		onRemove={(vehicleId) => handleRemoveVehicle(row.orderNumber, vehicleId)}
															/>
														</>
													)}
												</SortableRow>
											))
										)}
									</tbody>
								</table>
							</div>
						</SortableContext>
					</DndContext>
				</div>

				{/* Unlinked Items */}
				<div className="grid grid-cols-2 gap-4">
					{/* Unlinked Teachers */}
					<div className="border rounded p-4">
						<div className="flex justify-between items-center mb-3">
							<h3 className="text-md font-semibold">Giáo viên chưa liên kết ({unlinkedTeachers.length})</h3>
							<Button
								size="small"
								type="primary"
								icon={<Plus size={14} />}
								onClick={handleAddAllTeachers}
								disabled={unlinkedTeachers.length === 0}>
								Thêm tất cả
							</Button>
						</div>
						<div className="max-h-60 overflow-y-auto space-y-2">
							{unlinkedTeachers.length === 0 ? (
								<p className="text-gray-400 text-sm text-center py-4">Không có giáo viên</p>
							) : (
								unlinkedTeachers.map((teacher) => (
									<div key={teacher.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
										<DraggableTeacher teacher={teacher} isInLinkedTable={false} />
										<button
											onClick={() => handleAddTeacherToRow(teacher)}
											className="text-blue-500 hover:text-blue-700"
											title="Thêm vào bảng liên kết">
											<Plus size={16} />
										</button>
									</div>
								))
							)}
						</div>
					</div>

					{/* Unlinked Vehicles */}
					<div className="border rounded p-4">
						<div className="flex justify-between items-center mb-3">
							<h3 className="text-md font-semibold">Phương tiện chưa liên kết ({unlinkedVehicles.length})</h3>
							<Button
								size="small"
								type="primary"
								icon={<Plus size={14} />}
								onClick={handleAddAllVehicles}
								disabled={unlinkedVehicles.length === 0}>
								Thêm tất cả
							</Button>
						</div>
						<div className="max-h-60 overflow-y-auto space-y-2">
							{unlinkedVehicles.length === 0 ? (
								<p className="text-gray-400 text-sm text-center py-4">Không có phương tiện</p>
							) : (
								unlinkedVehicles.map((vehicle) => (
									<div key={vehicle.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
										<DraggableVehicle vehicle={vehicle} isInLinkedTable={false} />
										<button
											onClick={() => handleAddVehicleToRow(vehicle)}
											className="text-blue-500 hover:text-blue-700"
											title="Thêm vào bảng liên kết">
											<Plus size={16} />
										</button>
									</div>
								))
							)}
						</div>
					</div>
				</div>

				{/* Duplicate Section */}
				<div className="border-t pt-4">
					<h3 className="text-lg font-semibold mb-3">Sao chép GV và Xe</h3>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Chọn hạng đào tạo</label>
							<Select
								value={selectedLicenseClassId}
								onChange={(value) => setSelectedLicenseClassId(value)}
								placeholder="Chọn lớp giấy phép"
								className="w-full"
								loading={loadingLicenseClasses}>
								{licenseClasses.map((licenseClass) => (
									<Select.Option key={licenseClass.id} value={licenseClass.id}>
										{licenseClass.name}
									</Select.Option>
								))}
							</Select>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Chọn khóa học nguồn</label>
							<Select
								value={selectedTargetCourseId}
								onChange={(value) => setSelectedTargetCourseId(value)}
								placeholder="Chọn khóa học đích"
								className="w-full"
								loading={loadingCourses}>
								{courseList.map((course) => (
									<Select.Option key={course.id} value={course.id}>
										{course.name}
									</Select.Option>
								))}
							</Select>
						</div>
					</div>
					<div className="mt-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Tùy chọn sao chép</label>
						<Select
							value={duplicateOption}
							onChange={(value) => setDuplicateOption(value)}
							placeholder="Chọn tùy chọn sao chép"
							className="w-full">
							<Select.Option value={3}>Sao chép GV và Xe</Select.Option>
							<Select.Option value={1}>Chỉ sao chép GV</Select.Option>
							<Select.Option value={2}>Chỉ sao chép Xe</Select.Option>
						</Select>
					</div>
					<div className="mt-4">
						<Button
							type="primary"
							icon={<Copy size={16} />}
							loading={duplicating}
							onClick={handleDuplicate}
							disabled={!selectedLicenseClassId || !selectedTargetCourseId}>
							Sao chép
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default LinkTeacherVehicleModal;
